import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Wrench, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ProfessionService, getServiceLabel } from '@/services/professions/getServices';
import { Skeleton } from '@/components/ui/skeleton';

interface ServiceSelectProps {
  services: ProfessionService[];
  selectedService: ProfessionService | null;
  onSelect: (service: ProfessionService | null) => void;
  loading?: boolean;
}

const ServiceSelect: React.FC<ServiceSelectProps> = ({
  services,
  selectedService,
  onSelect,
  loading = false
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const currentLang = i18n.language;
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  // Build services with display names and searchable text from all languages
  const displayServices = useMemo(() => {
    return services.map(service => ({
      ...service,
      displayName: getServiceLabel(service, currentLang),
      // Combine all language variants for searching
      searchValue: [
        service.label,
        service.label_en,
        service.label_fr,
        service.label_ru
      ].filter(Boolean).join(' ')
    }));
  }, [services, currentLang]);

  // Filter services by search term
  const filteredServices = useMemo(() => {
    if (!searchTerm.trim()) return displayServices;
    const search = searchTerm.toLowerCase();
    return displayServices.filter(s => 
      s.searchValue.toLowerCase().includes(search) ||
      s.displayName.toLowerCase().includes(search)
    );
  }, [displayServices, searchTerm]);

  // Get display text for the button
  const buttonText = selectedService 
    ? getServiceLabel(selectedService, currentLang)
    : t('requestForm.selectService', { defaultValue: 'בחר שירות ספציפי (אופציונלי)' });

  if (loading) {
    return (
      <div className="space-y-2">
        <Label className="text-gray-700">
          {t('requestForm.selectService', { defaultValue: 'בחר שירות ספציפי (אופציונלי)' })}
        </Label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-gray-700">
        {t('requestForm.selectService', { defaultValue: 'בחר שירות ספציפי (אופציונלי)' })}
      </Label>
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label={t('requestForm.selectService', { defaultValue: 'בחר שירות ספציפי' })}
              className="flex-1 justify-between text-right pr-3 pl-10 relative rounded-lg"
            >
              <span className={selectedService ? '' : 'text-muted-foreground'}>
                {buttonText}
              </span>
              <Wrench className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-full min-w-[230px] bg-white shadow-lg z-50 p-0 border border-gray-300"
            align="start"
            side="bottom"
            avoidCollisions={false}
            style={{ overflow: 'visible' }}
          >
            <Command dir={direction}>
              <CommandInput
                placeholder={t('requestForm.searchService', { defaultValue: 'חפש שירות...' })}
                value={searchTerm}
                onValueChange={setSearchTerm}
                className="bg-white"
              />
              <CommandEmpty>
                {t('requestForm.noServiceFound', { defaultValue: 'לא נמצא שירות' })}
              </CommandEmpty>
              <CommandList className="bg-white z-50 max-h-64 overflow-auto border-gray-200">
                <CommandGroup>
                  {/* Skip option */}
                  <CommandItem
                    value="__skip__"
                    onSelect={() => {
                      onSelect(null);
                      setSearchTerm('');
                      setOpen(false);
                    }}
                    className="text-muted-foreground italic"
                  >
                    {t('requestForm.skipService', { defaultValue: 'לא רלוונטי - המשך ללא שירות ספציפי' })}
                  </CommandItem>
                  {/* Service options */}
                  {filteredServices.map(service => (
                    <CommandItem
                      key={service.id}
                      value={service.searchValue}
                      onSelect={() => {
                        onSelect(service);
                        setSearchTerm('');
                        setOpen(false);
                      }}
                    >
                      {service.displayName}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        
        {/* Clear button when service is selected */}
        {selectedService && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onSelect(null)}
            className="shrink-0"
            aria-label={t('common.cancel', { defaultValue: 'ביטול' })}
          >
            <X size={18} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ServiceSelect;
